<template>
  <el-form ref="form">
    <el-row :gutter="20">
      <el-col :span="12" :offset="6">
        <div class="label-contract">
          {{ $t('agent') }}:
        </div>
        <el-form-item>
          <el-select v-model="agentId"
                     :placeholder="$t('pleaseSelectAgent')"
                     :disabled="isShowFromAgent"
                     style="width: 100%"
                     filterable
                     @change="getContactPerson">
            <el-option
              v-for="(agent, index) in sortedAgentList"
              :key="index"
              :label="agent.name"
              :value="agent.id">
            </el-option>
          </el-select>
        </el-form-item>
        <div style="margin-top: 40px" v-if="sortedContactPersonList.length > 0">
          <div class="label-contract">
            {{ $t('contactPerson') }}:
          </div>
          <el-form-item>
            <div v-for="(contactPerson, index) in sortedContactPersonList" :key="index">
              <el-radio v-model="contactPersonId" :label="contactPerson.id" @change="onContactPersonChange">
                {{bindContactName(contactPerson)}}
                <strong v-if="contactPerson.isPrimary === 1"> * Primary</strong>
                <strong v-if="contactPerson.isSecondary === 1"> * Secondary</strong>
              </el-radio>
              <div class="contract-data-version" v-if="isUpdatedData(contactPersonList, contactPerson)">
                <div class="contract-data-updated">
                  <div style="padding-left: 20px">
                    <strong>*{{$t('updatedContactPersonMessage')}}</strong>
                  </div>
                  <el-radio-group style="padding-left: 40px" v-model="contactPersonDataVersion" fill="#ff0000" @change="onContactPersonChange">
                    <div class="contract-data-version-radio">
                      <el-radio v-model="contactPersonDataVersion" :label="DATA_VERSION.SELECTED">{{$t('selectedVersion')}} ({{bindUpdatedAt(contactPerson.updatedAt)}})</el-radio>
                    </div>
                    <div class="contract-data-version-radio">
                      <el-radio v-model="contactPersonDataVersion" :label="DATA_VERSION.LATEST">{{$t('latestVersion')}} ({{bindLatestUpdatedAt(contactPerson.id)}})</el-radio>
                    </div>
                  </el-radio-group>
                </div>
              </div>
              <div class="contract-data-version" v-if="isDeletedData(contactPersonList, contactPerson.id)">
                <div class="contract-data-deleted">
                  <div style="padding-left: 20px">
                    <strong style="color: #dd3333">*{{$t('deletedContactPersonMessage')}}</strong>
                  </div>
                  <el-radio-group style="padding-left: 40px" v-model="contactPerson.id" fill="#ff0000" >
                    <el-radio :label="contactPerson.id" class="contract-data-version-radio">{{$t('selectedVersion')}} ({{bindUpdatedAt(contactPerson.updatedAt)}})</el-radio>
                  </el-radio-group>
                </div>
              </div>
            </div>
          </el-form-item>
        </div>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
import { get, pull, uniq } from 'lodash'
import i18nMessages from '@/translations/Contract/components/TabContractAgent.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import AgentContactPersonApi from '@/api/agent/contact-person-api'
import { mapState } from 'vuex'
import { dateFormat } from '@/helpers/datetime'
import { isUpdatedData, isDeletedData, DATA_VERSION } from '@/services/version-service'
import { dynamicSort } from '@/helpers'

export default {
  name: 'TabAgent',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  i18n: { messages: i18nMessages },
  model: {
    prop: 'contractDetail',
    event: 'onAgentDetailChange'
  },
  props: {
    agentList: {
      type: Array,
      default: function () {
        return []
      }
    },
    contractDetail: {
      type: Object,
      default: function () {
        return {}
      }
    },
    isShowFromAgent: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      activeAgentId: state => state.general.activeAgentId
    })
  },
  created: async function () {
    this.sortedAgentList = this.agentList.sort(dynamicSort('name'))

    this.agentId = get(this.contractDetail, 'agent.id', '')
    if (this.isShowFromAgent === true) {
      this.agentId = this.activeAgentId
    }

    if (this.agentId === '') {
      return
    }

    this.tmpContractAgent = get(this.contractDetail, 'agent', {})
    this.contactPersonId = get(this.contractDetail, 'agent.contactPerson.id', '')
    this.contactPersonList = await AgentContactPersonApi.list(this.agentId)
    this.sortedContactPersonList = this.sortContactPerson()
  },
  data () {
    return {
      DATA_VERSION,
      isUpdatedData,
      isDeletedData,
      contactPersonDataVersion: DATA_VERSION.SELECTED,
      agentId: '',
      contactPersonId: '',
      tmpContractAgent: '',
      sortedContactPersonList: [],
      contactPersonList: [],
      sortedAgentList: []
    }
  },
  methods: {
    getContactPerson: async function () {
      this.contactPersonList = await AgentContactPersonApi.list(this.agentId)
      this.sortedContactPersonList = this.sortContactPerson()

      if (this.sortedContactPersonList.length > 0) {
        this.contactPersonId = this.sortedContactPersonList[0].id

        if (this.tmpContractAgent.id === this.agentId) {
          this.contactPersonId = this.tmpContractAgent.contactPerson.id
        }
      }

      this.agentDetailChange()
    },
    sortContactPerson: function () {
      const contactPersonDataList = this._getContactPersonDataSource()
      const contactPrimary = contactPersonDataList.find(item => item.isPrimary === 1)
      const contactSecondary = contactPersonDataList.find(item => item.isSecondary === 1)
      const contactOtherList = contactPersonDataList.filter(item => item.isPrimary !== 1 && item.isSecondary !== 1)

      contactOtherList.sort((prev, next) => (prev.firstName > next.firstName) ? 1 : -1)

      const resultContactPerson = [contactPrimary, contactSecondary, ...contactOtherList]
      const sortedContactPersonList = pull(resultContactPerson, undefined)
      const result = uniq(sortedContactPersonList)

      return result
    },
    bindContactName: function (contactPerson) {
      const firstName = get(contactPerson, 'firstName', '')
      const lastName = get(contactPerson, 'lastName', '')
      let position = get(contactPerson, 'position', '')
      let title = get(contactPerson, 'title', '')

      if (position !== '') {
        position = `(${position})`
      }

      return `${title} ${firstName} ${lastName} ${position}`
    },
    bindLatestUpdatedAt: function (contactPersonId) {
      const contact = this.contactPersonList.find(item => item.id === contactPersonId)

      if (contact !== undefined) {
        const date = new Date(contact.updatedAt)
        return dateFormat.long(date)
      }
    },
    bindUpdatedAt: function (updatedDate) {
      if (updatedDate === undefined) {
        return '-'
      }
      const date = new Date(updatedDate)
      return dateFormat.long(date)
    },
    onContactPersonChange: function () {
      this.agentDetailChange()
    },
    agentDetailChange: function () {
      const agent = this.agentList.find(item => item.id === this.agentId)
      const contactPerson = this._getContactResult()

      const agentData = Object.assign({}, agent, { contactPerson: contactPerson })
      const agentDetail = {
        agent: agentData
      }

      const contractDetail = Object.assign({}, this.contractDetail, agentDetail)
      this.$emit('onAgentDetailChange', contractDetail)
    },
    _getContactPersonDataSource: function () {
      if (this.tmpContractAgent.id === this.agentId) {
        this.contactPersonId = get(this.tmpContractAgent, 'contactPerson.id', '')
      }

      const contactPersonDataList = this._getContactPersonDataList()
      const contactDataResult = contactPersonDataList.find(item => item.id === this.contactPersonId)

      if (contactDataResult === undefined && this.agentId === this.contractDetail.agent.id) {
        if (this.contractDetail.agent.contactPerson === undefined) {
          return contactPersonDataList
        }

        const contactPersonWithDeletedList = [...contactPersonDataList, this.contractDetail.agent.contactPerson]
        return contactPersonWithDeletedList
      }

      return contactPersonDataList
    },
    _getContactPersonDataList: function () {
      if (this.tmpContractAgent.id === this.agentId) {
        this.contractDetail.agent = this.tmpContractAgent
      }

      const contractContactPerson = get(this.contractDetail.agent, 'contactPerson', {})
      const contractContactPersonId = get(contractContactPerson, 'id', '')

      const contactPersonDataList = this.contactPersonList.map((contactPerson) => {
        if (contactPerson.id !== contractContactPersonId) {
          return contactPerson
        }

        const priority = {
          isPrimary: contactPerson.isPrimary,
          isSecondary: contactPerson.isSecondary
        }

        const contactData = Object.assign({}, contractContactPerson, priority)
        return contactData
      })

      return contactPersonDataList
    },
    _getContactResult: function () {
      if (this.contactPersonDataVersion === DATA_VERSION.SELECTED) {
        const selectedContactPerson = this.sortedContactPersonList.find(item => item.id === this.contactPersonId)
        return selectedContactPerson
      }

      const latestContactPerson = this.contactPersonList.find(item => item.id === this.contactPersonId)
      return latestContactPerson
    }
  }
}
</script>

<style scoped>
  .contact-list-radio {
    padding: 10px 0;
  }

  .label-contract {
    font-weight: bold;
    margin: 10px 0;
  }
</style>
